import { useMemo } from 'react';
import { Link } from 'react-router-dom';

import { ActionItemKeys, NotificationResponse, ProviderNewClient } from '@uplift/api';
import { useTimezone } from '@uplift/contexts';
import {
  trackDismissToDo, trackToDoCTA, useAllActionItems, useAppointmentAssessments,
  useAppointmentIntakeAssessment, useL10n
} from '@uplift/hooks';
import { HandshakeIcon, NewClientIcon, VolunteerActivismIcon } from '@uplift/icons';
import { CalendarDateService, formatTime } from '@uplift/services';
import { ProviderMedicalRole, SubSystem, UserRole } from '@uplift/types';
import { Button, ExpandableDashboardActionItem } from '@uplift/ui-v2';

import { useFormKeys } from '../../../../hooks';
import { ClientFormProgressList } from './form-display/client-form-progress-list.component';
import { useAppointmentByIdQuery, useUserQuery } from '@uplift/queries';

export type NewClientActionItemProps = ProviderNewClient;

export const NewClientActionItem: React.VFC<NewClientActionItemProps> = client => {
  const l10n = useL10n();
  const formKeys = useFormKeys([ 'phq9', 'gad7' ]);
  const timezone = useTimezone();
  const appointmentDate = CalendarDateService.date(client.appointmentTime).tz(timezone);
  const formattedAppointmentDate = appointmentDate.format('MMM Do');
  const formattedAppointmentTime = formatTime(appointmentDate);
  const { data: appointment } = useAppointmentByIdQuery(client?.appointmentId);
  const { data: referringProvider } = useUserQuery(appointment?.createdBy, { enabled: !!appointment?.createdBy });
  const { appointmentAssessments, isLoading } = useAppointmentAssessments(client.appointmentId);
  const { appointmentIntakeAssessment, isLoading: intakeLoading } = useAppointmentIntakeAssessment(client.appointmentId);
  const { dismiss } = useAllActionItems();

  const { title, icon } = useMemo(() => {
    if (client?.referral?.fromUser.subsystem === SubSystem.higherEd) {
      return {
        title: l10n.dashboard.actionItems.partnerReferral.title(client.fullName),
        icon: <HandshakeIcon data-testid="partner-referral-icon" />
      };
    }
    if (referringProvider && referringProvider?.medicalRole === ProviderMedicalRole.therapist) {
      return {
        title: l10n.dashboard.actionItems.referral.title(client.fullName),
        icon: <VolunteerActivismIcon data-testid="in-platform-referral-icon" />
      };
    }
    return {
      title: l10n.dashboard.actionItems.newClient.title(client.fullName),
      icon: <NewClientIcon data-testid="new-client-icon" />
    };
  }, [ client.fullName, client?.referral?.fromUser.subsystem, l10n.dashboard.actionItems.newClient, l10n.dashboard.actionItems.partnerReferral, l10n.dashboard.actionItems.referral, referringProvider ]);

  const { badgeText, bodyText } = useMemo(() => {
    const assess = appointmentAssessments.some(x => x.requiresAttention);
    const requiresAttention = appointmentIntakeAssessment?.highRisk || assess;

    if (requiresAttention) {
      return {
        bodyText: l10n.dashboard.actionItems.newClient.recommendConsultation,
        badgeText: l10n.dashboard.actionItems.newClient.requiresAttention,
      };
    }

    return {
      bodyText: null,
      badgeText: null
    };
  }, [ appointmentAssessments, appointmentIntakeAssessment?.highRisk, l10n.dashboard.actionItems.newClient.recommendConsultation, l10n.dashboard.actionItems.newClient.requiresAttention ]);

  const handleDismiss = () => {
    dismiss({ key: ActionItemKeys.PROVIDER_NEW_CLIENTS, id: client.userId });
    trackDismissToDo({
      toDoType: ActionItemKeys.PROVIDER_NEW_CLIENTS,
      userType: UserRole.provider,
      reason: NotificationResponse.dismiss
    });
  };

  return (
    <ExpandableDashboardActionItem
      title={(
        <b>
          {title}
        </b>
      )}
      details={l10n.dashboard.actionItems.newClient.subtitle(formattedAppointmentDate, formattedAppointmentTime)}
      icon={icon}
      color="secondary"
      expandText={l10n.dashboard.actionItems.newClient.expandText}
      hideText={l10n.dashboard.actionItems.newClient.hideText}
      badge={badgeText}
      body={bodyText}
      testId="new-client-action-item"
      actionButton={(
        <Button
          fullWidth
          variant="contained"
          color="secondary"
          component={Link}
          to={`/clients/${client.userId}`}
          onClick={() => trackToDoCTA({
            toDoType: ActionItemKeys.PROVIDER_NEW_CLIENTS,
            userType: UserRole.provider
          })}
          data-testid="new-client-action-item-cta"
        >
          {l10n.dashboard.actionItems.newClient.actionText}
        </Button>
      )}
      dismiss={{
        text: l10n.dashboard.actionItems.newClient.dismissText,
        handleClick: handleDismiss
      }}
    >
      <ClientFormProgressList
        appointmentAssessments={appointmentAssessments}
        appointmentIntakeAssessment={appointmentIntakeAssessment}
        isLoading={isLoading || intakeLoading}
        patient={client}
        gad7Id={formKeys?.gad7}
        phq9Id={formKeys?.phq9}
      />
    </ExpandableDashboardActionItem>
  );
};
